import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface MonthlyTargets {
  appointment_sets: number;
  opening_appointments: number;
  closing_appointments: number;
  lives_closed: number;
}

export interface DailyTargets {
  appointment_sets: number;
  opening_appointments: number;
  closing_appointments: number;
  lives_closed: number;
}

export interface AnnualTargets {
  appointment_sets: number;
  opening_interviews: number;
  closing_interviews: number;
  life_cases_needed: number;
}

export interface PledgeSheetInput {
  name: string;
  saved_by_name?: string;
  minimum_fyc: number;
  stretched_fyc: number;
  average_case_size: number;
  closing_rate: number;
  opening_to_closing_rate: number;
  call_to_opening_rate: number;
  monthly_targets?: MonthlyTargets;
  daily_targets?: DailyTargets;
  pledge_year?: number | null;
  selected_months?: number | null;
  annual_appointment_sets?: number | null;
  annual_opening_interviews?: number | null;
  annual_closing_interviews?: number | null;
  annual_life_cases_needed?: number | null;
}

export interface PledgeSheet extends PledgeSheetInput {
  id: string;
  user_id: string | null;
  created_at: string;
  updated_at: string;
}

const fetchPledgeSheets = async (userId: string): Promise<PledgeSheet[]> => {
  const { data, error } = await (supabase as any)
    .from('pledge_sheets')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching pledge sheets:', error);
    throw error;
  }

  return data || [];
};

export function usePledgeSheetsQuery() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAuthenticated = !!user;

  const { data: pledgeSheets = [], isLoading: loading } = useQuery({
    queryKey: ['pledge_sheets', user?.id],
    queryFn: () => fetchPledgeSheets(user!.id),
    enabled: !!user,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: async (pledgeSheetData: PledgeSheetInput) => {
      const dataToInsert = {
        ...pledgeSheetData,
        user_id: isAuthenticated ? user?.id : null,
      };

      const { data, error } = await (supabase as any)
        .from('pledge_sheets')
        .insert([dataToInsert])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pledge_sheets'] });
      toast({
        title: "Success",
        description: "Pledge sheet saved successfully!",
      });
    },
    onError: (error) => {
      console.error('Error saving pledge sheet:', error);
      toast({
        title: "Error",
        description: "Failed to save pledge sheet. Please try again.",
        variant: "destructive",
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<PledgeSheetInput> }) => {
      const { data, error } = await (supabase as any)
        .from('pledge_sheets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pledge_sheets'] });
      toast({
        title: "Success",
        description: "Pledge sheet updated successfully!",
      });
    },
    onError: (error) => {
      console.error('Error updating pledge sheet:', error);
      toast({
        title: "Error",
        description: "Failed to update pledge sheet. Please try again.",
        variant: "destructive",
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('pledge_sheets')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pledge_sheets'] });
      toast({
        title: "Success",
        description: "Pledge sheet deleted successfully!",
      });
    },
    onError: (error) => {
      console.error('Error deleting pledge sheet:', error);
      toast({
        title: "Error",
        description: "Failed to delete pledge sheet. Please try again.",
        variant: "destructive",
      });
    }
  });

  return {
    pledgeSheets,
    loading,
    savePledgeSheet: (data: PledgeSheetInput) => saveMutation.mutateAsync(data),
    updatePledgeSheet: (id: string, updates: Partial<PledgeSheetInput>) =>
      updateMutation.mutateAsync({ id, updates }),
    deletePledgeSheet: (id: string) => deleteMutation.mutateAsync(id),
    fetchPledgeSheets: () => queryClient.invalidateQueries({ queryKey: ['pledge_sheets'] }),
  };
}
